/**
 * App Layout with Responsive Sidebar Navigation and Header
 */
import React, { useState, useEffect } from 'react';
import { NotificationBell } from './NotificationBell';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import useAuthStore from '../lib/auth-store';
import ImpersonationBanner from './ImpersonationBanner';
import LowStockAlert from './LowStockAlert';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Sparkles,
  LayoutDashboard, Users, MapPin, GraduationCap, FileText, 
  ShoppingCart, DollarSign, FileCheck, UsersRound, ScrollText, 
  MessageSquare, Settings, LogOut, Search, Bell, Plus, Calendar, Mail,
  Building2, Shield, Activity, CreditCard, ClipboardCheck, Calculator, Percent, Package,
  BarChart3, Menu, X, HelpCircle, UserCog, Landmark, Monitor, Banknote, ClipboardList, Scale,
  FileEdit, UserPlus, TrendingUp, FileSearch, Sun, Moon, Store, PenLine, Crosshair, ListTodo, CalendarRange, ShieldCheck, Compass, ChevronDown, ChevronRight, ExternalLink
} from 'lucide-react';

const ExternalIcon = () => <ExternalLink className="h-3 w-3 ml-auto opacity-40" />;
import useThemeStore from '../lib/theme-store';
import AIAssistantBubble from './AIAssistantBubble';
import { useDemoTour } from './tour/DemoTourContext';
import { DemoTourOverlay } from './tour/DemoTourOverlay';
import useTenantBrandStore from '../lib/tenant-brand-store';
import { WeeklyDigestModal } from './tasks/WeeklyDigestModal';
import api from '../lib/api';
import { Walkthrough } from '../pages/FeatureGuidePage';
import { AnnouncementBanner } from './comm/Announcements';
import { Megaphone, Images, Eraser, Reply } from 'lucide-react';

const ADMIN_AREA_HREFS = {
  compensation: ['/admin/schedule-a-master', '/admin/schedule-a-templates', '/admin/agreements', '/admin/residuals', '/schedule-a', '/admin/onboard-partner'],
  payouts: ['/payout-run', '/admin/payer-payout-runs', '/admin/payout-responsibility', '/payer-economics', '/reconciliation', '/admin/residual-analyses', '/schedule-approvals'],
  banking: ['/payment-profiles', '/payments', '/admin/payment-programs', '/admin/partner-billing'],
  tax_identity: ['/admin/ein-reviews', '/kyc-queue'],
  platform_settings: ['/settings', '/admin/menu-links', '/admin/tenants', '/admin/jarvis-knowledge', '/system-health', '/admin/users', '/admin/audit-logs', '/admin/hardware-markup', '/admin/residual-settings'],
};

const SUPER_ONLY_HREFS = ['/admin/team'];

const AreaOff = () => (
  <div className="mx-auto mt-16 max-w-md rounded-lg border p-8 text-center" data-testid="admin-area-off">
    <ShieldCheck className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
    <p className="font-medium">This area isn't switched on for your account</p>
    <p className="mt-1 text-sm text-muted-foreground">Ask a Super Admin to turn it on in Admin Team.</p>
    <Link to="/dashboard" className="mt-4 inline-block text-sm text-primary hover:underline" data-testid="admin-area-off-home">Back to dashboard</Link>
  </div>
);

function AppLayout() {
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const { applyUserBranding } = useTenantBrandStore();
  const location = useLocation();
  const navigate = useNavigate();
  const tour = useDemoTour();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showDigest, setShowDigest] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [profileHeader, setProfileHeader] = useState(null);
  const [collapsed, setCollapsed] = useState(() => { try { return JSON.parse(localStorage.getItem('nav_collapsed') || '{}'); } catch { return {}; } });
  const toggleSection = (name) => setCollapsed((c) => { const next = { ...c, [name]: !c[name] }; localStorage.setItem('nav_collapsed', JSON.stringify(next)); return next; });
  useEffect(() => { if (user) api.request('/me/profile-header').then(setProfileHeader).catch(() => setProfileHeader(null)); }, [user]);
  const [adminAccess, setAdminAccess] = useState(null);
  useEffect(() => { if (user?.role === 'super_admin') api.request('/me/admin-access').then(setAdminAccess).catch(() => setAdminAccess({ level: null, areas: {} })); }, [user]);
  const [activationGate, setActivationGate] = useState(false);
  useEffect(() => {
    if (!user || user.role === 'super_admin') return;
    api.request('/activation/me').then((a) => {
      const gate = a.required && !a.crm_access_ready;
      setActivationGate(gate);
      if (gate && !['/activate', '/agreement', '/announcements', '/my-settings', '/my-residual-analysis', '/billing'].includes(location.pathname)) navigate('/activate');
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname]);
  const [customLinks, setCustomLinks] = useState([]);
  const [effectivePermissions, setEffectivePermissions] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.getMyMenuLinks().then(setCustomLinks).catch(() => setCustomLinks([]));
    api.getMyEffectivePermissions().then((d) => setEffectivePermissions(d.effective || {})).catch(() => setEffectivePermissions({}));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const isMonday = new Date().getDay() === 1;
    if (!isMonday) return;
    api.getDigestStatus().then((s) => { if (!s.dismissed) setShowDigest(true); }).catch(() => {});
  }, [user]);

  // Branding from user's tenant
  const brandPrimary = user?.tenant_primary_color || '#3b82f6';
  const brandAccent = user?.tenant_accent_color || '#10b981';
  const brandSidebar = user?.tenant_sidebar_color || '#ffffff';
  const brandLogo = user?.tenant_logo;
  const tenantName = user?.tenant_name || 'ISOCNTRL';
  const tenantFont = user?.tenant_font || 'Inter';

  // Detect if sidebar background is dark → use white text
  const isDarkSidebar = (() => {
    const hex = brandSidebar.replace('#', '');
    if (hex.length !== 6) return theme === 'dark';
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.45;
  })();

  // Force dark sidebar detection for dark mode too
  const sidebarIsDark = isDarkSidebar || theme === 'dark';
  const sidebarTextColor = sidebarIsDark ? '#ffffff' : undefined;
  const sidebarMutedColor = sidebarIsDark ? 'rgba(255,255,255,0.55)' : undefined;
  const sidebarBorderColor = sidebarIsDark ? 'rgba(255,255,255,0.1)' : undefined;

  // Apply user branding to the store (for favicon, title, etc.)
  useEffect(() => {
    if (user) applyUserBranding(user);
  }, [user, applyUserBranding]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isSuperAdmin = user?.role === 'super_admin';
  const isIsoAdmin = user?.role === 'iso';

  const customLinksSection = customLinks.length > 0
    ? [
        { type: 'section', name: 'CUSTOM LINKS' },
        ...customLinks.map((l) => ({
          name: l.title,
          href: l.open_mode === 'iframe' ? `/embed/${l.id}` : l.url,
          icon: LayoutDashboard,
          external: l.open_mode !== 'iframe',
          openInNewTab: l.open_mode === 'new_tab',
          allow_camera: l.allow_camera,
          allow_microphone: l.allow_microphone,
          _custom_link: l,
        })),
      ]
    : [];

  // Filter nav by effective permissions (super_admin always sees all).
  // Items without a `feature` key are always shown. Section headers are dropped
  // if none of the items that follow them (until the next section) survive.
  const filterByPermissions = (items) => {
    if (user?.role === 'super_admin') {
      if (adminAccess?.level !== 'assistant_manager') return items;
      const off = Object.entries(ADMIN_AREA_HREFS).filter(([a]) => !adminAccess.areas?.[a]).flatMap(([, h]) => h);
      return items.filter((it) => !it.href || !off.some((h) => it.href === h || it.href.startsWith(`${h}/`)));
    }
    if (!effectivePermissions) return items;
    // First pass: filter items by feature key
    const passed = items
      .map((item) => {
        if (item.type === 'group' && item.children) {
          const filteredChildren = item.children.filter(
            (c) => !c.feature || effectivePermissions[c.feature] !== false,
          );
          if (item.feature && effectivePermissions[item.feature] === false) return null;
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        if (item.feature && effectivePermissions[item.feature] === false) return null;
        return item;
      })
      .filter(Boolean);
    // Second pass: drop orphaned section headers (no items follow them)
    const compact = [];
    for (let i = 0; i < passed.length; i++) {
      const it = passed[i];
      if (it.type === 'section') {
        const nextNonSection = passed.slice(i + 1).find((x) => x.type !== 'section' && x.type !== 'divider');
        if (!nextNonSection) continue;
      }
      compact.push(it);
    }
    return compact;
  };

  const role = user?.role;
  const isManager = isSuperAdmin || isIsoAdmin;
  const canBuild = isSuperAdmin || isIsoAdmin || (role === 'iso_agent' && profileHeader?.can_build_downline);
  const sec = (name, items) => (items.length ? [{ type: 'section', name }, ...items] : []);
  const rawNavigation = [
    ...sec('CRM', [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard, feature: 'dashboard' },
      { name: 'Announcements', href: '/announcements', icon: Megaphone },
      { name: 'My Day', href: '/tasks', icon: ListTodo, feature: 'my_day' },
      { name: 'Sales GPS', href: '/sales-gps', icon: Compass, feature: 'sales_gps' },
      { name: 'Weekly Planner', href: '/planner', icon: CalendarRange, feature: 'weekly_planner' },
      { name: 'Prospecting', href: '/prospecting', icon: Crosshair, feature: 'prospecting' },
      { name: 'Leads', href: '/leads', icon: Users, feature: 'leads' },
      { name: 'Merchants', href: '/merchants', icon: Store, feature: 'merchants' },
      { name: 'Statement Analysis', href: '/statement-analysis', icon: FileSearch, feature: 'statement_analysis' },
      { name: 'Applications', href: '/applications', icon: FileCheck, feature: 'applications' },
      { name: 'Calendar', href: '/calendar', icon: Calendar, feature: 'calendar' },
      { name: 'Communications', href: '/communications', icon: Mail, feature: 'communications' },
      { name: 'Reply Coach', href: '/reply-coach', icon: Reply, feature: 'communications' },
      { name: 'Map View', href: '/map', icon: MapPin, feature: 'map_view' },
      { name: 'Documents', href: '/documents', icon: FileText, feature: 'documents' },
      { name: 'E-Signatures', href: '/esignatures', icon: PenLine, feature: 'esignatures' },
      { name: 'My Residuals', href: '/residuals', icon: Percent, feature: 'residuals' },
      ...(isSuperAdmin ? [] : [{ name: 'My MSC Deposit', href: '/my-residual-analysis', icon: Calculator }, { name: 'My Billing', href: '/billing', icon: CreditCard }]),
    ]),
    ...sec('PIPELINES', [
      { name: 'Merchant Services', href: '/pipeline/merchant_services', icon: Landmark },
      { name: 'POS Systems', href: '/pipeline/pos', icon: Monitor },
      { name: 'Lending', href: '/pipeline/lending', icon: Banknote },
    ]),
    ...sec('TEAM', canBuild ? [
      { name: 'Team & Users', href: isSuperAdmin ? '/admin/users' : '/users', icon: UsersRound, feature: 'team_users' },
      { name: 'Invite to Team', href: '/team-invite', icon: UserPlus },
      { name: 'Activation Tracker', href: '/activation-tracker', icon: ClipboardCheck },
      { name: 'Schedule A', href: '/schedule-a', icon: FileText, feature: 'schedule_a' },
      ...(isManager ? [{ name: 'Activity Logs', href: '/activity-logs', icon: Activity, feature: 'activity_logs' }] : []),
    ] : []),
    ...sec(isManager ? 'MANAGEMENT' : 'TOOLS', [
      ...(isManager ? [
        { name: 'Equipment', href: '/equipment', icon: ShoppingCart, feature: 'equipment' },
        { name: 'Orders', href: '/orders', icon: ClipboardList, feature: 'orders' },
        { name: 'Processor & Boarding', href: '/processor', icon: Landmark, feature: 'processor' },
        { name: 'Form Builder', href: '/leads/form-builder', icon: FileEdit, feature: 'lead_forms' },
        { name: 'Affiliates', href: '/affiliates', icon: UserPlus, feature: 'affiliates' },
        { name: 'Conversion Funnel', href: '/funnel', icon: TrendingUp, feature: 'conversion_funnel' },
      ] : []),
      { name: 'Commission Calculator', href: '/residuals/calculator', icon: Calculator, feature: 'commission_calculator' },
      { name: 'E-Learning', href: '/elearning', icon: GraduationCap, feature: 'elearning' },
      ...(isManager ? [{ name: 'Settings', href: '/settings', icon: Settings }] : []),
    ]),
    ...sec('RESIDUALS & FINANCE', isManager ? [
      { name: 'Payer Economics', href: '/payer-economics', icon: TrendingUp },
      { name: 'Payout Run', href: '/payout-run', icon: Banknote },
      ...(isSuperAdmin ? [{ name: 'Payout Responsibility', href: '/admin/payout-responsibility', icon: Scale },
        { name: 'Payer Payout Runs', href: '/admin/payer-payout-runs', icon: Banknote },
        { name: 'Relationships & Components', href: '/admin/economic-relationships', icon: Scale }] : []),
      ...(isIsoAdmin ? [{ name: 'My Downline Payout Run', href: '/my-payout-run', icon: Banknote },
        { name: 'My Managed Agents', href: '/my-managed-agents', icon: Users }] : []),
      { name: 'Schedule Approvals', href: '/schedule-approvals', icon: ClipboardCheck },
      { name: 'Reconciliation', href: '/reconciliation', icon: Scale },
      { name: 'Payee Banking', href: '/payment-profiles', icon: Landmark },
      { name: 'Payments', href: '/payments', icon: CreditCard, feature: 'payments' },
    ] : []),
    ...sec('PAYOUTS', role === 'iso_agent' ? [
      ...(profileHeader?.can_build_downline ? [{ name: 'My Downline Payout Run', href: '/my-payout-run', icon: Banknote }] : []),
      { name: 'My Managed Agents', href: '/my-managed-agents', icon: Users },
    ] : []),
    ...sec('OPERATIONS', isManager ? [
      { name: 'KYC Queue', href: '/kyc-queue', icon: ShieldCheck, feature: 'kyc_queue' },
      { name: 'Duplicate Review', href: '/duplicate-review', icon: Users, feature: 'duplicate_review' },
      { name: 'MID Health', href: '/mid-health', icon: Activity, feature: 'mid_health' },
      { name: 'Processor Connections', href: '/processor-connections', icon: Activity },
      { name: 'System Health', href: '/system-health', icon: Shield, feature: 'system_health' },
    ] : []),
    ...sec('PLATFORM (MSC)', isSuperAdmin ? [
      { name: 'Platform Overview', href: '/admin', icon: Shield },
      { name: 'Onboard a Partner', href: '/admin/onboard-partner', icon: UserPlus },
      { name: 'Partner Billing', href: '/admin/partner-billing', icon: CreditCard },
      ...(adminAccess?.level === 'super_admin' ? [{ name: 'Admin Team', href: '/admin/team', icon: ShieldCheck }] : []),
      { name: 'Tenants', href: '/admin/tenants', icon: Building2 },
      { name: 'Enrollment Requests', href: '/admin/enrollment', icon: UserPlus },
      { name: 'Agreements', href: '/admin/agreements', icon: PenLine },
      { name: 'Announcements (MSC)', href: '/admin/announcements', icon: Megaphone },
      { name: 'Image Studio', href: '/admin/image-studio', icon: Images },
      { name: 'Clear Sample Data', href: '/admin/sample-data', icon: Eraser },
      { name: 'MSC Schedule A', href: '/admin/schedule-a-master', icon: DollarSign },
      { name: 'Schedule A Templates', href: '/admin/schedule-a-templates', icon: DollarSign },
      { name: 'EIN Reviews', href: '/admin/ein-reviews', icon: FileCheck },
      { name: 'Residual Analysis', href: '/admin/residual-analyses', icon: FileSearch },
      { name: 'MSC-MBI Provisioning', href: '/admin/provisioning', icon: ClipboardCheck },
      { name: 'Merchant Approvals', href: '/admin/merchant-approvals', icon: ClipboardCheck },
      { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
      { name: 'Audit Logs', href: '/admin/audit-logs', icon: Activity },
      { name: 'Fee Templates', href: '/admin/residuals', icon: DollarSign },
      { name: 'Hardware Markup', href: '/admin/hardware-markup', icon: Package },
      { name: 'Jarvis Knowledge', href: '/admin/jarvis-knowledge', icon: Sparkles },
      { name: 'Payment Programs', href: '/admin/payment-programs', icon: DollarSign },
      { name: 'Menu Links', href: '/admin/menu-links', icon: LayoutDashboard },
    ] : []),
    ...customLinksSection,
    ...sec('ACCOUNT', [
      { name: 'My Settings', href: '/my-settings', icon: UserCog },
      { name: 'Help & Support', href: '/help', icon: HelpCircle },
      { name: 'Feature Guide', href: '/help/feature-guide', icon: GraduationCap },
    ]),
  ];

  const navigation = (() => {
    const out = [];
    let current = null;
    for (const item of filterByPermissions(rawNavigation)) {
      if (item.type === 'section') { current = item.name; out.push(item); continue; }
      if (!collapsed[current] || location.pathname === item.href) out.push(item);
    }
    return out;
  })();

  const closeSidebar = () => setSidebarOpen(false);

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="h-16 px-6 flex items-center space-x-3 flex-shrink-0" style={{ borderBottom: `1px solid ${sidebarBorderColor || ''}` }}>
        {brandLogo ? (
          <img src={brandLogo} alt={tenantName} className="h-8 w-auto max-w-[140px] object-contain rounded-lg" data-testid="sidebar-tenant-logo" />
        ) : (
          <>
            <img src="/isocntrl-logo-400.png" alt="ISOCNTRL" className="h-8 w-auto object-contain" data-testid="sidebar-tenant-logo" />
          </>
        )}
        {/* Mobile close button */}
        <button onClick={closeSidebar} className="lg:hidden ml-auto p-1 rounded hover:bg-white/10" style={sidebarTextColor ? { color: sidebarTextColor } : undefined} data-testid="sidebar-close-btn">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* User Profile — role and compensation title are separate fields shown on one line */}
      <div className="p-4 flex-shrink-0" style={{ borderBottom: `1px solid ${sidebarBorderColor || ''}` }} data-testid="profile-header">
        <div className="flex items-center space-x-3">
          <label className="relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer overflow-hidden" title="Change photo" style={{ backgroundColor: sidebarIsDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)' }} data-testid="profile-photo">
            {profileHeader?.photo_url
              ? <img src={`${process.env.REACT_APP_BACKEND_URL}${profileHeader.photo_url}`} alt="" className="h-full w-full object-cover" />
              : <span className="text-sm font-semibold" style={{ color: sidebarIsDark ? '#ffffff' : undefined }}>{(user?.first_name?.[0] || user?.full_name?.[0] || 'U').toUpperCase()}</span>}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="profile-photo-input" onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              const fd = new FormData(); fd.append('file', f);
              try { await api.upload('/me/photo', fd); setProfileHeader(await api.request('/me/profile-header')); } catch { /* toast handled by api */ }
            }} />
          </label>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate flex items-center gap-1" style={sidebarTextColor ? { color: sidebarTextColor } : undefined} data-testid="profile-name">
              {user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.full_name || 'User'}
              {profileHeader?.promotion_star && (
                <span title={`New Tier Achieved! Promoted to ${profileHeader.promotion_star.label}, ${new Date(profileHeader.promotion_star.achieved_at).toLocaleDateString()}`}
                      style={{ color: brandPrimary }} className="cursor-help" data-testid="promotion-star">★</span>
              )}
            </p>
            <p className="text-xs truncate" style={{ color: sidebarMutedColor || undefined }} data-testid="profile-role-title">
              <span data-testid="profile-role">{adminAccess?.level === 'assistant_manager' ? 'Assistant Manager' : (profileHeader?.role_label || user?.role?.replace(/_/g, ' '))}</span>
              {profileHeader?.compensation_tier_label && <> – <span data-testid="profile-tier-label">{profileHeader.compensation_tier_label}</span></>}
            </p>
            <p className="text-[11px] truncate" style={{ color: sidebarMutedColor || undefined }}>{profileHeader?.organization || user?.tenant_name || ''}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navigation.map((item, idx) => {
          if (item.type === 'divider') {
            return <div key={`div-${idx}`} className="my-2" style={{ borderTop: `1px solid ${sidebarBorderColor || ''}` }} />;
          }
          if (item.type === 'section') {
            return (
              <button type="button" key={`sec-${idx}`} onClick={() => toggleSection(item.name)} data-testid={`nav-section-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      className="w-full flex items-center justify-between text-[10px] uppercase tracking-wider px-3 pt-3 pb-1 font-semibold" style={{ color: sidebarMutedColor || undefined }}>
                <span>{item.name}</span>{collapsed[item.name] ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            );
          }
          if (item.type === 'group') {
            const hasActiveChild = item.children.some(
              (c) => location.pathname === c.href || (c.href.length > 1 && location.pathname.startsWith(c.href))
            );
            const isExpanded = expandedGroups[item.name] ?? hasActiveChild;
            const GroupIcon = item.icon;
            return (
              <div key={`grp-${item.name}`}>
                <button
                  type="button"
                  onClick={() => setExpandedGroups((s) => ({ ...s, [item.name]: !isExpanded }))}
                  data-testid={`nav-group-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="w-full flex items-center justify-between space-x-3 px-3 py-2.5 rounded-lg transition-colors text-sm"
                  style={hasActiveChild
                    ? { backgroundColor: sidebarIsDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', color: sidebarIsDark ? '#ffffff' : undefined }
                    : { color: sidebarIsDark ? 'rgba(255,255,255,0.7)' : undefined }
                  }
                  onMouseEnter={(e) => { if (sidebarIsDark) e.currentTarget.style.color = '#ffffff'; }}
                  onMouseLeave={(e) => { if (sidebarIsDark && !hasActiveChild) e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
                >
                  <span className="flex items-center space-x-3">
                    <GroupIcon className="h-5 w-5 flex-shrink-0" />
                    <span className="font-medium">{item.name}</span>
                  </span>
                  {isExpanded
                    ? <ChevronDown className="h-4 w-4 opacity-60" />
                    : <ChevronRight className="h-4 w-4 opacity-60" />}
                </button>
                {isExpanded && (
                  <div className="ml-6 mt-0.5 space-y-0.5" style={{ borderLeft: `1px solid ${sidebarBorderColor || 'rgba(0,0,0,0.08)'}` }}>
                    {item.children.map((child) => {
                      const isChildActive = location.pathname === child.href ||
                        (child.href.length > 1 && location.pathname.startsWith(child.href));
                      const ChildIcon = child.icon;
                      return (
                        <Link
                          key={child.name}
                          to={child.href}
                          onClick={closeSidebar}
                          data-testid={`nav-${child.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          className={`flex items-center space-x-3 pl-3 pr-3 py-2 rounded-r-lg transition-colors text-sm ${isChildActive ? 'text-white' : ''}`}
                          style={isChildActive
                            ? { backgroundColor: brandPrimary }
                            : { color: sidebarIsDark ? 'rgba(255,255,255,0.65)' : undefined }
                          }
                          onMouseEnter={(e) => { if (!isChildActive && sidebarIsDark) e.currentTarget.style.color = '#ffffff'; }}
                          onMouseLeave={(e) => { if (!isChildActive && sidebarIsDark) e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                        >
                          <ChildIcon className="h-4 w-4 flex-shrink-0 opacity-80" />
                          <span className="font-medium">{child.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          const isActive = location.pathname === item.href ||
            (item.href.length > 1 && item.href !== '/admin' && location.pathname.startsWith(item.href));
          const IconComponent = item.icon;
          if (item.external) {
            return (
              <a
                key={item.name}
                href={item.href}
                target={item.openInNewTab ? '_blank' : '_self'}
                rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                data-testid={`nav-custom-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors text-sm"
                style={{ color: sidebarIsDark ? 'rgba(255,255,255,0.7)' : undefined }}
                onMouseEnter={(e) => { if (sidebarIsDark) e.currentTarget.style.color = '#ffffff'; }}
                onMouseLeave={(e) => { if (sidebarIsDark) e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
              >
                <IconComponent className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium">{item.name}</span>
                <ExternalIcon />
              </a>
            );
          }
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={closeSidebar}
              data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
              className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                isActive ? 'text-white' : ''
              }`}
              style={isActive
                ? { backgroundColor: brandPrimary }
                : { color: sidebarIsDark ? 'rgba(255,255,255,0.7)' : undefined }
              }
              onMouseEnter={(e) => { if (!isActive && sidebarIsDark) e.currentTarget.style.color = '#ffffff'; }}
              onMouseLeave={(e) => { if (!isActive && sidebarIsDark) e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
            >
              <IconComponent className="h-5 w-5 flex-shrink-0" />
              <span className="font-medium">{item.name}</span>
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-4 flex-shrink-0" style={{ borderTop: `1px solid ${sidebarBorderColor || ''}` }}>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="w-full justify-start"
          style={sidebarIsDark ? { color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.15)', background: 'transparent' } : undefined}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      <ImpersonationBanner />
      {user?.is_sandbox && (
        <div className="bg-emerald-600 text-white text-sm px-4 py-2 flex items-center justify-center gap-3 flex-wrap" data-testid="sandbox-mode-banner">
          <span className="font-semibold">Sandbox Demo Mode</span>
          <span className="hidden sm:inline text-emerald-100">— all data here is fictitious and resets nightly</span>
          <button
            onClick={() => tour?.start()}
            className="underline font-medium hover:text-emerald-100"
            data-testid="restart-tour-btn"
          >
            Restart Guided Tour
          </button>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 lg:hidden"
          onClick={closeSidebar}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar — fixed on desktop, slide-in on mobile */}
      <div
        className={`fixed inset-y-0 left-0 w-64 flex flex-col z-50 transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: brandSidebar, borderRight: `1px solid ${sidebarBorderColor || 'var(--border)'}` }}
      >
        <SidebarContent />
      </div>

      {/* Main Content Area with Header */}
      <div className="lg:pl-64">
        {/* Top Header Bar */}
        <div className="h-14 sm:h-16 border-b border-border bg-card px-4 sm:px-6 flex items-center justify-between sticky top-0 z-40">
          {/* Mobile hamburger */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-muted"
            data-testid="sidebar-toggle-btn"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Search */}
          <div className="flex-1 max-w-xl hidden sm:block">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search leads, applications, documents..."
                className="pl-9 bg-background"
                data-testid="global-search-input"
              />
            </div>
          </div>

          {/* Mobile: brand title */}
          <span className="font-heading font-bold text-base sm:hidden" data-testid="mobile-brand-title">{tenantName}</span>

          {/* Actions */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="relative"
              data-testid="theme-toggle-btn"
              title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5 text-yellow-400" />}
            </Button>
            <NotificationBell />
            <Button
              onClick={() => navigate('/leads/new')}
              className="text-white hidden sm:flex"
              style={{ backgroundColor: brandPrimary }}
              data-testid="new-lead-header-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Lead
            </Button>
            <Button
              onClick={() => navigate('/leads/new')}
              className="text-white sm:hidden"
              size="icon"
              style={{ backgroundColor: brandPrimary }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-4 sm:p-6">
          <AnnouncementBanner />
          {user?.role === 'super_admin' && !adminAccess ? <p className="p-6 text-sm text-muted-foreground" data-testid="admin-access-loading">Loading…</p>
            : adminAccess?.level === 'assistant_manager' && [...SUPER_ONLY_HREFS, ...Object.entries(ADMIN_AREA_HREFS).filter(([a]) => !adminAccess.areas?.[a]).flatMap(([, h]) => h)]
            .some((h) => location.pathname === h || location.pathname.startsWith(`${h}/`)) ? <AreaOff /> : <Outlet />}
          {!activationGate && location.pathname !== '/activate' && <Walkthrough />}
        </div>
      </div>

      {/* Low Stock Alert Popup — Super Admin Only */}
      <LowStockAlert />

      {/* AI Assistant Floating Bubble */}
      <AIAssistantBubble />

      {/* Weekly Digest — Monday morning popup */}
      <WeeklyDigestModal open={showDigest} onClose={() => setShowDigest(false)} />

      {/* Sandbox guided tour overlay */}
      {user?.is_sandbox && <DemoTourOverlay />}
    </div>
  );
}

export default AppLayout;
